import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PoseSetup from './PoseSetup';
import StyleTile, { gridSx } from './StyleTile';
import { stepPropTypes, stepDefaultProps } from './stepProps';

// Step 5. The two style settings the old editor named "Package hero" and "Display
// styles" without ever saying how they differ. Distinguished here by WHERE the
// customer sees each -- and shown as real previews, because a checkbox list of
// names like "FALL27-02" is unpickable.

const Presentation = ({
  product, choices, commit, setOverrides, preview, busy,
}) => {
  const styles = choices.styles || [];
  // Looks that render to a specific pose, grouped by it and in pose order; and the
  // ones that declare no pose at all.
  const unbound = styles.filter((st) => !st.pose_bound);
  const sample = preview && preview.samplePose;

  // WHICH package everything below applies to. A photo package is a collection of
  // choices -- "Ultimate Collection" may include a graduation card that "Two 8x10s"
  // does not -- so the styles AND the pose order belong to a choice, not to the whole
  // product. '' is the product's list, which every choice without its own inherits
  // (Shop::ProductSerializer.template_ids_for_variant, Package#poses_for).
  const variants = product.variants || [];
  const [scope, setScope] = useState('');
  const scoped = variants.find((v) => String(v.id) === String(scope)) || null;
  const offered = (scoped ? scoped.style_ids : product.style_ids) || [];
  const required = !!(scoped ? scoped.style_required : product.style_required);

  // The pose list this choice actually shows: its own once it has one, otherwise the
  // product's. Sent whole in the payload, so changing the selector needs no round trip
  // (Shop::WizardPayload#variant_pose_lists).
  // Card art first in the card-image grid, everything else after, each half in its
  // existing order (#5444).
  const heroStyles = styles.filter((st) => st.hero_layout);
  const cardStyles = heroStyles.length
    ? [...heroStyles, ...styles.filter((st) => !st.hero_layout)]
    : styles;

  const ownPoses = (scoped && (product.variant_pose_lists || {})[String(scoped.id)]) || null;
  const poseProduct = ownPoses ? { ...product, ...ownPoses } : product;

  const setCard = (id) => {
    setOverrides((o) => ({ ...o, promo_template_id: id || null }));
    commit({ hero_template_id: id ? String(id) : '' });
  };

  // The preview renders the product, so only an unscoped edit is worth showing
  // optimistically -- a per-choice list would make the preview claim something the
  // card does not show.
  const commitStyles = (ids, wantRequired) => {
    if (!scoped) setOverrides((o) => ({ ...o, template_ids: ids }));
    commit({
      ...(scoped ? { variant_id: String(scoped.id) } : {}),
      template_ids: ids.map(String),
      template_required: wantRequired ? '1' : '0',
    });
  };

  const toggleStyle = (id) => commitStyles(
    offered.includes(id) ? offered.filter((x) => x !== id) : [...offered, id],
    required,
  );

  // Only for a product with more than one package: for a single print or a
  // download, "which choice" has one answer and the selector would be noise.
  const scopePicker = variants.length > 1 ? (
    <Paper sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Settings for"
          value={scope}
          // Without displayEmpty MUI draws the field blank for the '' value, so the
          // default reads as "nothing chosen" when it is the setting that applies.
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 300, flex: '0 0 auto' }}
          disabled={busy}
          onChange={(e) => setScope(e.target.value)}
        >
          <MenuItem value="">Every choice (the product&rsquo;s list)</MenuItem>
          {variants.map((v) => (
            <MenuItem key={v.id} value={String(v.id)}>
              {`${v.name}${v.own_styles ? ' · own list' : ''}`}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {scoped && (scoped.own_styles || ownPoses)
              ? `This choice has its own ${[scoped.own_styles ? 'styles' : null,
                ownPoses ? 'pose order' : null].filter(Boolean).join(' and ')}. `
                + 'The product\u2019s applies to the rest.'
              : null}
            {scoped && !scoped.own_styles && !ownPoses
              ? 'Inheriting the product\u2019s styles and pose order \u2014 changing anything '
                + 'below sets them for this choice only.'
              : null}
            {scoped ? null : 'Applies to every choice that has none of its own.'}
          </Typography>
        </Box>
        {scoped && scoped.own_styles ? (
          <Button
            size="small"
            disabled={busy}
            onClick={() => commit({ variant_id: String(scoped.id), inherit: '1' })}
          >
            Use the product&rsquo;s list
          </Button>
        ) : null}
      </Stack>
    </Paper>
  ) : null;

  // Backgrounds and pose order are independent of photo styles, and this step used
  // to RETURN EARLY when a season had no published styles -- which would hide them
  // on exactly the products most likely to need them.
  const backgroundPicker = (
    <Box>
      <Typography variant="subtitle2">
        Backgrounds — what the customer can choose behind their portrait
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {product.background_set_id
          ? `${product.background_count} background${product.background_count === 1 ? '' : 's'} offered.`
          : 'None yet — the store shows no background behind the portrait, and the order records none.'}
      </Typography>
      <TextField
        select
        label="Background set"
        value={product.background_set_id || ''}
        sx={{ maxWidth: 470 }}
        onChange={(e) => commit({ background_set_id: e.target.value })}
      >
        <MenuItem value="">— none —</MenuItem>
        {(choices.background_sets || []).map((set) => (
          <MenuItem key={set.id} value={set.id}>
            {`${set.name} · ${set.count} background${set.count === 1 ? '' : 's'}`}
            {set.in_season ? ' · used this season' : ''}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  // Ordering only, and partial: the store puts declared poses first in this order
  // and everything else after by number, so a short list means "show these first"
  // rather than "hide the rest". It never deletes a pose row either -- one can carry
  // a background chosen in the legacy admin, and the live senior package has 98.
  // Order, background and look for a pose are one subject -- pose number joins a
  // PackagePose to the LookTemplate that renders to it -- so they are one table
  // rather than three sections to scroll between. See PoseSetup.
  const poseSetup = (
    <PoseSetup
      product={poseProduct}
      scopedVariantId={scoped ? String(scoped.id) : null}
      inheriting={!!scoped && !ownPoses}
      choices={choices}
      commit={commit}
      sample={sample}
      busy={busy}
      offered={offered}
      onToggleLook={toggleStyle}
    />
  );

  if (!styles.length) {
    return (
      <Stack spacing={2}>
        <Alert
          severity="info"
          action={(
            <Button
              color="inherit"
              href="/admin/shop/templates"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the style designer
            </Button>
          )}
        >
          {`No published photo styles exist for ${product.season || 'this season'}, `
            + 'so this product will show the student\'s plain photo.'}
        </Alert>
        {backgroundPicker}
        {poseSetup}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {backgroundPicker}
      {scopePicker}
      {poseSetup}
      <Box>
        <Typography variant="subtitle2">
          Card image — the picture on the product card while customers choose
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          {heroStyles.length
            ? 'Layouts marked as card art in the style designer come first. Any published style '
              + 'can be used, including one you do not offer below.'
            : 'Affects the card only. Can be any published style, including one you do not offer below.'}
        </Typography>
        <Box sx={gridSx}>
          <StyleTile
            style={null}
            sample={sample}
            label="Plain photo"
            on={!product.card_image_id}
            onClick={() => setCard(null)}
          />
          {/* Hero layouts first. A season can hold 90+ published looks, and hunting a
              card image through all of them is what the designer's Hero Layouts
              section exists to end -- but nothing is hidden, because the flag says
              "drawn to be card art", not "the only thing allowed here". */}
          {cardStyles.map((st) => (
            <StyleTile
              key={st.id}
              style={st}
              sample={sample}
              on={product.card_image_id === st.id}
              onClick={() => setCard(st.id)}
            />
          ))}
        </Box>
      </Box>

      <Box>
        {/* An unbound look has no pose to sit under in the table above, so it keeps
            its own block. */}

        {/* A look with no pose number cannot be resolved for any real pose -- none is
            numbered 0 -- so a customer who picks one is charged for a style the
            pipeline has nothing to apply. Three LIVE seniors2026 products offer
            exactly one such look, so this is said, not blocked. */}
        {unbound.length ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {`${unbound.length} look${unbound.length === 1 ? '' : 's'} here `
                + `${unbound.length === 1 ? 'has' : 'have'} no pose number. Fulfilment looks `
                + 'a style up by the pose it renders to, so a customer picking one of these '
                + 'would be charged for a style the pipeline cannot apply. Give it a pose '
                + 'number in the style designer.'}
            </Typography>
            <Box sx={gridSx}>
              {unbound.map((st) => (
                <StyleTile
                  key={st.id}
                  style={st}
                  sample={sample}
                  on={offered.includes(st.id)}
                  onClick={() => toggleStyle(st.id)}
                />
              ))}
            </Box>
          </Alert>
        ) : null}
      </Box>

      <Paper sx={{ p: 1.5 }}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={required}
              disabled={busy}
              onChange={(e) => commitStyles(offered, e.target.checked)}
            />
          )}
          label={(
            <Typography variant="body2">
              {scoped
                ? `Sell “${scoped.name}” styled, never as a plain photo`
                : 'Sell this styled, never as a plain photo'}
            </Typography>
          )}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
          {/* Customers stopped picking a style in the builder (#5448), so this no longer
              removes an option from them -- it decides what a package with no look of
              its own sells as. */}
          {scoped
            ? 'For a choice that only makes sense styled, like a graduation card. Without a look '
              + 'of its own it uses the first offered above. The store hides the choice if none '
              + 'of its styles are available.'
            : 'For products that only make sense styled, like a graduation card. Without a look '
              + 'of its own it uses the first offered above. The store hides such a product '
              + 'entirely if none of its styles are available.'}
        </Typography>
      </Paper>
    </Stack>
  );
};

Presentation.propTypes = stepPropTypes;
Presentation.defaultProps = stepDefaultProps;

export default Presentation;
